// Imported from the modules directly rather than the `generic` barrel, which also exports the
// `MetricChart` component: pulling that into a pure spec builder would drag a React component
// tree and its stylesheets along with it.
import { dayAxis, weekAxis } from '@/components/charts/generic/dayAxis';
import type { MetricSeriesSpec } from '@/components/charts/generic/seriesTypes';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { graphSettings } from '@/config/settings';
import type { Locale } from '@/utils/localeFormatters';

import {
    type LiceChartDay,
    type LiceChartMetric,
    METRIC_COLORS,
    METRIC_FIELDS,
} from './liceChartMetrics';

/**
 * Chart specs for the lice pages.
 *
 * Kept beside the pages rather than in `overviewCharts/`, which is scoped to the cage overview
 * and whose twelve builders are all about biomass. The card those pages render — the loading,
 * error, full-screen and PNG plumbing — is shared rather than copied.
 *
 * ## Why this replaces `OptoMixedChart`
 *
 * The legacy mapper builds its labels from the days that **have rows**, so two measurements a
 * week apart are drawn adjacent and joined by a straight line: the chart shows a value on days
 * nothing was measured. {@link dayAxis} fills the axis with every calendar day in the range
 * instead, so a gap stays a gap.
 *
 * That matters more than it looks. The backend deliberately imputes nothing — a day without a
 * row produces no row — and the fish-group views drop one day at every relocation, so a cohort
 * that moves pen has a hole by construction. Drawing a line across it would invent a lice
 * level on a day nobody counted.
 */

export interface LiceCountChartSpecParams {
    days: LiceChartDay[];
    metrics: LiceChartMetric[];
    /** All already translated. */
    labels: Record<LiceChartMetric, string> & {
        liceAxis: string;
        temperatureAxis: string;
        temperature: string;
    };
    locale: Locale;
    /**
     * The licensed maximum, drawn as a reference line when the cage's licence exposes one.
     * Omitted rather than drawn at zero when it does not.
     */
    maximumLiceLimit?: number;
    /** Already translated. */
    maximumLiceLimitLabel?: string;
}

const LICE_TICK_DECIMALS = 2;
const LICE_VALUE_DECIMALS = 2;
const TEMPERATURE_SUGGESTED_MAX = 20;
const TEMPERATURE_STEP_SIZE = 5;

/**
 * Lice per fish on the left, sea temperature on the right.
 *
 * The one chart every lice page draws, differing only in which counts it shows — which is why
 * `metrics` is a parameter rather than there being four builders.
 */
export function buildLiceCountChartSpec(params: LiceCountChartSpecParams): MetricChartSpec<'line'> {
    const { days, metrics, labels, locale, maximumLiceLimit, maximumLiceLimitLabel } = params;

    const liceSeries: MetricSeriesSpec[] = metrics.map((metric) => ({
        key: METRIC_FIELDS[metric],
        label: labels[metric],
        color: METRIC_COLORS[metric],
        data: days.map((day) => ({ day: day.day, value: day[METRIC_FIELDS[metric]] ?? null })),
    }));

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: [
            ...liceSeries,
            {
                key: 'temperature',
                label: labels.temperature,
                color: graphSettings.graphColors.temperature,
                axis: 'right',
                fill: true,
                // A backdrop for the lice counts drawn over it, so it fades downwards rather
                // than sitting behind them as one flat block.
                fillGradient: true,
                data: days.map((day) => ({ day: day.day, value: day.temperature ?? null })),
            },
        ],
        left: {
            label: labels.liceAxis,
            beginAtZero: true,
            tickDecimals: LICE_TICK_DECIMALS,
            valueDecimals: LICE_VALUE_DECIMALS,
        },
        right: {
            label: labels.temperatureAxis,
            unit: '°C',
            beginAtZero: true,
            suggestedMax: TEMPERATURE_SUGGESTED_MAX,
            stepSize: TEMPERATURE_STEP_SIZE,
            tickDecimals: 0,
            valueDecimals: 0,
        },
        // Absent rather than zero when the cage has no maximum-count licence: a limit line at
        // zero would read as "the limit is zero", which is the opposite of "there is no limit
        // to show".
        ...(maximumLiceLimit !== undefined && maximumLiceLimitLabel !== undefined
            ? {
                  referenceLines: [
                      {
                          key: 'maximum-lice-limit',
                          value: maximumLiceLimit,
                          label: maximumLiceLimitLabel,
                          axis: 'left' as const,
                          color: graphSettings.alertColors.danger,
                      },
                  ],
              }
            : {}),
        locale,
    };
}

// ---------------------------------------------------------------------------
// Lice per fish — the same counts as bars, daily or by ISO week
// ---------------------------------------------------------------------------

export interface LicePerFishChartSpecParams {
    days: LiceChartDay[];
    metrics: LiceChartMetric[];
    /**
     * Drawn but switched off until the reader asks for them. The daily chart starts with only
     * the headline count visible; the weekly one shows every series.
     */
    hiddenByDefault?: LiceChartMetric[];
    /** Keys the axis by ISO week rather than by day. The keys stay the week's Monday. */
    weekly?: boolean;
    labels: Record<LiceChartMetric, string> & { liceAxis: string };
    locale: Locale;
}

const LICE_PER_FISH_STEP_SIZE = 0.1;

/**
 * Lice per fish as bars.
 *
 * Replaces `DailyLiceCountToLicePerFishCountBarChartMapper` and its `inactiveSetLegend`, which
 * is `hiddenByDefault` here — the same idea with a name that says what it does.
 */
export function buildLicePerFishChartSpec(
    params: LicePerFishChartSpecParams
): MetricChartSpec<'bar'> {
    const { days, metrics, hiddenByDefault = [], weekly = false, labels, locale } = params;

    return {
        kind: 'bar',
        xAxisKind: weekly ? 'week' : 'day',
        // A weekly row is keyed by the Monday of its week, so the axis steps a week at a time.
        // Through `dayAxis` it stepped a day at a time and every one of the seven dates in a
        // week rendered the same `W32` tick.
        days: weekly ? weekAxis(days, (day) => day.day) : dayAxis(days, (day) => day.day),
        series: metrics.map((metric) => ({
            key: METRIC_FIELDS[metric],
            label: labels[metric],
            color: METRIC_COLORS[metric],
            hiddenByDefault: hiddenByDefault.includes(metric),
            data: days.map((day) => ({ day: day.day, value: day[METRIC_FIELDS[metric]] ?? null })),
        })),
        left: {
            label: labels.liceAxis,
            beginAtZero: true,
            stepSize: LICE_PER_FISH_STEP_SIZE,
            tickDecimals: LICE_TICK_DECIMALS,
            valueDecimals: LICE_VALUE_DECIMALS,
        },
        locale,
    };
}

export { buildFishCountChartSpec, type FishCountChartSpecParams } from './fishCountChartSpec';
export type { LiceChartDay, LiceChartMetric } from './liceChartMetrics';
